import React, { useState, useEffect, useContext } from 'react';
import Layout from '../../components/admin/Layout';
import { FaTags, FaList, FaUsers, FaDollarSign } from 'react-icons/fa';
import axios from '../../utils/axios';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { LanguageContext } from '../../context/LanguageContext';
import { toast } from 'react-hot-toast';

const getColorClasses = (color) => {
    switch (color) {
        case 'indigo':
            return {
                text: 'text-indigo-600',
                bg: 'bg-indigo-50',
                border: 'border-indigo-100'
            };
        case 'emerald':
            return {
                text: 'text-emerald-600',
                bg: 'bg-emerald-50',
                border: 'border-emerald-100'
            };
        case 'sky':
            return {
                text: 'text-sky-600',
                bg: 'bg-sky-50',
                border: 'border-sky-100'
            };
        case 'rose':
            return {
                text: 'text-rose-600',
                bg: 'bg-rose-50',
                border: 'border-rose-100'
            };
        default:
            return {
                text: 'text-gray-600',
                bg: 'bg-gray-50',
                border: 'border-gray-100'
            };
    }
};

const StatCard = ({ icon, title, value, color }) => {
    const colorClasses = getColorClasses(color);
    return (
        <div className={`bg-white rounded-xl p-6 shadow-sm border ${colorClasses.border} hover:shadow-md transition-shadow duration-200`}>
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-medium text-gray-600">{title}</h3>
                    <p className={`text-3xl font-bold mt-2 ${colorClasses.text}`}>{value}</p>
                </div>
                <div className={`${colorClasses.bg} p-4 rounded-full`}>
                    <span className={`${colorClasses.text} text-2xl`}>{icon}</span>
                </div>
            </div>
        </div>
    );
};

const Dashboard = () => {
    const { language } = useContext(LanguageContext);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState({
        stats: {
            categories: 0,
            items: 0,
            users: 0,
            revenue: 0
        },
        recent_orders: [],
        daily_revenue: [],
        top_items: []
    });

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const response = await axios.get('/dashboard');
                setDashboardData(response.data.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                toast.error(
                    language === 'ar' 
                        ? 'فشل في تحميل بيانات لوحة التحكم' 
                        : 'Failed to load dashboard data'
                );
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [language]);

    const stats = [
        { 
            icon: <FaTags />, 
            title: language === 'ar' ? 'الأقسام' : 'Categories', 
            value: formatNumber(dashboardData.stats.categories, language), 
            color: 'indigo'
        },
        { 
            icon: <FaList />, 
            title: language === 'ar' ? 'المنتجات' : 'Menu Items', 
            value: formatNumber(dashboardData.stats.items, language), 
            color: 'emerald'
        },
        { 
            icon: <FaUsers />, 
            title: language === 'ar' ? 'المستخدمين' : 'Users', 
            value: formatNumber(dashboardData.stats.users, language), 
            color: 'sky'
        },
        { 
            icon: <FaDollarSign />, 
            title: language === 'ar' ? 'الإيرادات' : 'Revenue', 
            value: formatCurrency(dashboardData.stats.revenue, language), 
            color: 'rose'
        },
    ];

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-screen">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-800">
                    {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
                </h1>
                <p className="text-gray-600 mt-1">
                    {language === 'ar' 
                        ? 'مرحباً بك في لوحة تحكم المطعم' 
                        : 'Welcome to your restaurant dashboard'}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                    <StatCard key={index} {...stat} />
                ))}
            </div>

            {/* Recent Orders Section */}
            <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                    {language === 'ar' ? 'الطلبات الأخيرة' : 'Recent Orders'}
                </h2>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {language === 'ar' ? 'رقم الطلب' : 'Order #'}
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {language === 'ar' ? 'العميل' : 'Customer'}
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {language === 'ar' ? 'المنتجات' : 'Items'}
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {language === 'ar' ? 'المجموع' : 'Total'}
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {language === 'ar' ? 'الحالة' : 'Status'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {dashboardData.recent_orders.map((order) => (
                                <tr key={order.id} className="hover:bg-gray-50 transition-colors duration-150">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {order.order_number}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {order.customer_name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {formatNumber(order.items_count, language)} {language === 'ar' ? 'منتج' : 'items'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {formatCurrency(order.total, language)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-50 text-green-600">
                                            {language === 'ar' 
                                                ? 'مكتمل' 
                                                : order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
};

export default Dashboard;
